import type { IMultiCheckboxSubFormConfig } from "./MultiCheckboxSubForm.types";
import { dateFormat, i18nCrossUtil } from "@adgm/libs";

const crossIntl = i18nCrossUtil.t();

/**
 * IndividualSourceOfFundForm 配置对象
 * 用于驱动通用 MultiCheckboxSubForm 组件
 * 所有文案从 Figma 设计稿中提取：https://www.figma.com/design/YIlGbY9AZh7tW4MZvzFrZN/Aspen-web?node-id=10255-19089&m=dev
 */
export const INDIVIDUAL_SOURCE_OF_FUND_CONFIG: IMultiCheckboxSubFormConfig = {
  options: [
    {
      label: () => crossIntl["kyc.v2.option.label.salary.bonus.from.employment"],
      fieldKey: "employmentIncome",
      subFormFields: [
        {
          name: "employerName",
          label: () => crossIntl["kyc.v2.form.label.name.of.employer"],
          type: "input",
          required: true,
        },
        {
          name: "businessSector",
          label: () => crossIntl["kyc.v2.form.label.business.sector"],
          type: "input",
          required: true,
        },
        {
          name: "annualSalaryAndBonus",
          label: () => crossIntl["kyc.v2.form.label.current.annual.salary.and.bonus"],
          type: "input",
          required: true,
        },
        {
          name: "employerCountry",
          label: () => crossIntl["kyc.v2.form.label.country.of.employer"],
          type: "input",
          required: true,
        },
      ],
      createInitialValue: () => ({
        employerName: "",
        businessSector: "",
        annualSalaryAndBonus: "",
        employerCountry: "",
      }),
    },
    {
      label: () => crossIntl["kyc.v2.option.label.inheritance.simple"],
      fieldKey: "inheritance",
      subFormFields: [
        {
          name: "deceasedName",
          label: () => crossIntl["kyc.v2.form.label.name.of.deceased"],
          type: "input",
          required: true,
        },
        {
          name: "relationToDeceased",
          label: () => crossIntl["kyc.v2.form.label.relation.to.deceased"],
          type: "input",
          required: true,
        },
        {
          name: "receiptDate",
          label: () => crossIntl["kyc.v2.form.label.date.of.receiving.inheritance"],
          type: "datePicker",
          required: true,
          dateFormat,
        },
        {
          name: "amountReceived",
          label: () => crossIntl["kyc.v2.form.label.amount.received"],
          type: "input",
          required: true,
        },
      ],
      createInitialValue: () => ({
        deceasedName: "",
        relationToDeceased: "",
        receiptDate: "",
        amountReceived: "",
      }),
    },
    {
      label: () => crossIntl["kyc.v2.option.label.family.support.gifts"],
      fieldKey: "familySupportGift",
      subFormFields: [
        {
          name: "donorName",
          label: () => crossIntl["kyc.v2.form.label.name.of.donor"],
          type: "input",
          required: true,
        },
        {
          name: "relationToDonor",
          label: () => crossIntl["kyc.v2.form.label.relation.to.donor"],
          type: "input",
          required: true,
        },
        {
          name: "amountReceived",
          label: () => crossIntl["kyc.v2.form.label.amount.received"],
          type: "input",
          required: true,
        },
      ],
      createInitialValue: () => ({
        donorName: "",
        relationToDonor: "",
        amountReceived: "",
      }),
    },
    {
      label: () => crossIntl["kyc.v2.option.label.retirement.income"],
      fieldKey: "retirementIncome",
      subFormFields: [
        {
          name: "lastEmployerName",
          label: () => crossIntl["kyc.v2.form.label.name.of.last.employer"],
          type: "input",
          required: true,
        },
        {
          name: "annualAmount",
          label: () => crossIntl["kyc.v2.form.label.amount.received.per.annum.simple"],
          type: "input",
          required: true,
        },
        {
          name: "issuingEntityName",
          label: () => crossIntl["kyc.v2.form.label.name.of.entity.or.authority.issuing.payment"],
          type: "input",
          required: true,
        },
      ],
      createInitialValue: () => ({
        lastEmployerName: "",
        annualAmount: "",
        issuingEntityName: "",
      }),
    },
    {
      label: () => crossIntl["kyc.v2.option.label.sale.of.assets.fund"],
      fieldKey: "assetSale",
      subFormFields: [
        {
          name: "assetDescription",
          label: () => crossIntl["kyc.v2.form.label.description.of.asset"],
          type: "input",
          required: true,
        },
        {
          name: "saleDate",
          label: () => crossIntl["kyc.v2.form.label.sale.date"],
          type: "datePicker",
          required: true,
          dateFormat,
        },
        {
          name: "saleAmount",
          label: () => crossIntl["kyc.v2.form.label.sale.amount"],
          type: "input",
          required: true,
        },
      ],
      createInitialValue: () => ({
        assetDescription: "",
        saleDate: "",
        saleAmount: "",
      }),
    },
    {
      label: () => crossIntl["kyc.v2.option.label.dividends.profits.portfolio"],
      fieldKey: "investmentIncome",
      subFormFields: [
        {
          name: "assetDescription",
          label: () => crossIntl["kyc.v2.form.label.description.of.investment.assets"],
          type: "input",
          required: true,
        },
        {
          name: "investmentName",
          label: () => crossIntl["kyc.v2.form.label.name.of.investments"],
          type: "input",
          required: true,
        },
        {
          name: "purchaseDate",
          label: () => crossIntl["kyc.v2.form.label.purchased.date"],
          type: "datePicker",
          required: true,
          dateFormat,
        },
        {
          name: "annualProfitAndDividends",
          label: () => crossIntl["kyc.v2.form.label.amount.received.per.annum"],
          type: "input",
          required: true,
        },
      ],
      createInitialValue: () => ({
        assetDescription: "",
        investmentName: "",
        purchaseDate: "",
        annualProfitAndDividends: "",
      }),
    },
    {
      label: () => crossIntl["kyc.other.input"],
      fieldKey: "other",
      isOther: true,
      otherPlaceholder: () => crossIntl["kyc.other.input"],
      otherTitleFieldName: "title",
      subFormFields: [
        {
          name: "description",
          label: () => crossIntl["kyc.v2.form.label.description.of.source.of.funds"],
          type: "input",
          required: true,
        },
        {
          name: "annualAmount",
          label: () => crossIntl["kyc.v2.form.label.approximate.amounts.per.annum"],
          type: "input",
          required: true,
        },
        {
          name: "countryOfOrigin",
          label: () => crossIntl["kyc.v2.form.label.country.of.origin"],
          type: "input",
          required: true,
        },
      ],
      createInitialValue: () => ({
        title: "",
        description: "",
        annualAmount: "",
        countryOfOrigin: "",
      }),
    },
  ],
};
